<template>
    <!-- Présentation des modules Prévisions & Performance et Tableau de bord sur l'accueil.
         Visible jusqu'au DATE_FIN incluse, uniquement pour les modules que l'utilisateur a le droit
         d'ouvrir (mêmes permissions que le menu de gauche), et masquable par l'utilisateur. -->
    <section v-if="visible" class="box po">
        <div class="po__tete">
            <div class="po__titre">
                <span class="po__pastille"><i class="ri-rocket-2-line"></i></span>
                <div>
                    <h2>{{ modules.length > 1 ? 'Deux outils pour piloter votre activité' : 'Un nouvel outil pour piloter votre activité' }}</h2>
                    <p>{{ modules.length > 1 ? "Ils viennent d'arriver dans votre application. Voici ce qu'ils vous apportent." : "Il vient d'arriver dans votre application. Voici ce qu'il vous apporte." }}</p>
                </div>
            </div>
            <button type="button" class="po__fermer" title="Ne plus afficher cette présentation" @click="masquer">
                <i class="ri-close-line"></i>Ne plus afficher
            </button>
        </div>

        <div class="po__grille" :class="{ 'po__grille--seul': modules.length === 1 }">
            <article v-for="m in modules" :key="m.id" class="po-mod" :class="'po-mod--' + m.id">
                <div class="po-mod__tete">
                    <span class="po-mod__ico"><i :class="m.icone"></i></span>
                    <div>
                        <div class="po-mod__nom">{{ m.nom }} <span class="po-badge">NOUVEAU</span></div>
                        <div class="po-mod__promesse">{{ m.promesse }}</div>
                    </div>
                </div>
                <ul class="po-mod__liste">
                    <li v-for="a in m.avantages" :key="a.titre">
                        <i :class="a.icone"></i>
                        <div><b>{{ a.titre }}</b><span>{{ a.texte }}</span></div>
                    </li>
                </ul>
                <div class="po-mod__pied">
                    <span class="po-mod__ou"><i class="ri-map-pin-line"></i> Menu : {{ m.menu }}</span>
                    <router-link :to="{ name: m.route }" class="po-btn"><i class="ri-arrow-right-line"></i>{{ m.bouton }}</router-link>
                </div>
            </article>
        </div>

        <div class="po__bas">
            <span><i class="ri-time-line"></i>Présentation visible jusqu'au {{ dateFinLisible }}, elle disparaîtra ensuite d'elle-même.</span>
            <span><i class="ri-information-line"></i>« Ne plus afficher » la masque seulement pour vous, sur ce poste.</span>
        </div>
    </section>
</template>

<script>
import permissionService from "@/service/directives/permissionService";
import * as JwtService from "@/common/jwt.service";

/* Dernier jour d'affichage (inclus) : un mois après la mise en service, le 2026-10-23. */
const DATE_FIN = "2026-11-23";
const CLE_MASQUE = "accueil_outils_pilotage_masque_v1";

const MODULES = [
    {
        id: "prev",
        permission: "parametres",               // même permission que l'entrée du menu
        route: "tprevisionperformance",
        nom: "Prévisions & Performance",
        menu: "Prévisions & Performance",
        icone: "ri-line-chart-line",
        bouton: "Découvrir mes prévisions",
        promesse: "Savoir à l'avance ce que vous allez vendre, ce que cela rapporte, et quoi racheter.",
        avantages: [
            { icone: "ri-funds-line", titre: "Votre chiffre des 30 prochains jours", texte: "Le montant le plus probable, avec une fourchette entre une mauvaise et une bonne période, et le bénéfice net une fois les charges déduites." },
            { icone: "ri-calendar-2-line", titre: "Vos jours forts et la semaine à venir", texte: "Quels jours vous vendez le plus, et ce que chacun des 7 prochains jours devrait rapporter." },
            { icone: "ri-shield-check-line", titre: "Des prévisions dont vous connaissez la fiabilité", texte: "Chaque mois passé est comparé à ce qui avait été prévu, avec un verdict clair." },
            { icone: "ri-pie-chart-2-line", titre: "Vos produits champions, et ceux vendus à perte", texte: "Les articles qui font votre chiffre, leur marge, et ceux sans prix d'achat ou vendus sous leur coût." },
            { icone: "ri-truck-line", titre: "Plus de rupture de stock", texte: "Combien de jours chaque article tient encore, combien en commander, et la commande prête à exporter." }
        ]
    },
    {
        id: "tb",
        permission: "tableau-bord",
        route: "tableaudebord",
        nom: "Tableau de bord",
        menu: "Tableau de bord",
        icone: "ri-dashboard-3-line",
        bouton: "Ouvrir le tableau de bord",
        promesse: "L'essentiel de votre activité en un coup d'œil, et beaucoup plus rapide.",
        avantages: [
            { icone: "ri-flashlight-line", titre: "Il s'ouvre en un instant", texte: "Les chiffres restent quelques minutes sur votre poste ; le bouton Actualiser recharge les derniers quand vous le voulez." },
            { icone: "ri-safe-2-line", titre: "Trésorerie et caisses d'un coup d'œil", texte: "Recettes, dépenses, caisses ouvertes et écarts de caisse, sur la période de votre choix." },
            { icone: "ri-archive-stack-line", titre: "Votre stock valorisé au vrai prix d'achat", texte: "La valeur réelle de votre marchandise, et non plus au prix de vente." },
            { icone: "ri-alarm-warning-line", titre: "Les alertes de stock en tête", texte: "Les articles à surveiller sont listés directement, sans attendre le chargement complet du stock." },
            { icone: "ri-percent-line", titre: "Votre marge bénéficiaire suivie", texte: "Ce que vous gagnez réellement sur vos ventes, période par période." }
        ]
    }
];

/* Date locale AAAA-MM-JJ (pas toISOString, qui passe en UTC). */
const aujourdhui = () => {
    const d = new Date(), p = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
};

export default {
    name: "OutilsPilotage",
    data() {
        return {
            masque: false,
            // incrémenté quand les permissions arrivent ou changent, pour recalculer les cartes
            versionPermissions: 0
        };
    },
    computed: {
        modules() {
            void this.versionPermissions;
            return MODULES.filter((m) => permissionService.hasPermission(m.permission));
        },
        visible() {
            return !this.masque && aujourdhui() <= DATE_FIN && this.modules.length > 0;
        },
        dateFinLisible() {
            const [a, m, j] = DATE_FIN.split("-");
            return `${j}/${m}/${a}`;
        }
    },
    mounted() {
        try { this.masque = localStorage.getItem(this.cle()) === "1"; } catch (e) { this.masque = false; }
        window.addEventListener("permissions-updated", this.permissionsChangees);
    },
    beforeUnmount() {
        window.removeEventListener("permissions-updated", this.permissionsChangees);
    },
    methods: {
        permissionsChangees() { this.versionPermissions++; },
        // une clé par utilisateur, comme les nouveautés lues de l'accueil
        cle() {
            let qui = "";
            try { qui = String(this.$store.state.appusername || JwtService.getTokenID() || ""); } catch (e) { qui = ""; }
            return CLE_MASQUE + (qui ? "_" + qui : "");
        },
        masquer() {
            this.masque = true;
            try { localStorage.setItem(this.cle(), "1"); } catch (e) { /* stockage indisponible : masqué pour cette visite */ }
        }
    }
};
</script>

<style scoped>
.po { position: relative; overflow: hidden; border: 1px solid rgb(var(--primary) / .25); background: linear-gradient(135deg, rgb(var(--primary) / .07), #fff 45%); }
.po__tete { display: flex; flex-wrap: wrap; gap: .75rem; align-items: center; justify-content: space-between; padding: 1.1rem 1.25rem .4rem; }
.po__titre { display: flex; align-items: center; gap: .6rem; }
.po__pastille { width: 2.4rem; height: 2.4rem; border-radius: .6rem; display: grid; place-items: center; background: rgb(var(--primary)); color: #fff; font-size: 1.2rem; flex-shrink: 0; }
.po__titre h2 { margin: 0; font-size: 1.05rem; font-weight: 700; color: #0f172a; }
.po__titre p { margin: .1rem 0 0; font-size: .8rem; color: #64748b; }
.po__fermer { border: 0; background: transparent; color: #94a3b8; font-size: .76rem; cursor: pointer; display: inline-flex; align-items: center; gap: .25rem; }
.po__fermer:hover { color: rgb(var(--danger)); }
.po__grille { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; padding: .75rem 1.25rem 1.1rem; }
.po__grille--seul { grid-template-columns: 1fr; }
.po-mod { background: #fff; border: 1px solid #e2e8f0; border-radius: .625rem; display: flex; flex-direction: column; }
.po-mod__tete { display: flex; gap: .7rem; align-items: flex-start; padding: 1rem 1rem .6rem; }
.po-mod__ico { width: 2.6rem; height: 2.6rem; border-radius: 50%; display: grid; place-items: center; font-size: 1.25rem; flex-shrink: 0; background: rgb(var(--primary) / .1); color: rgb(var(--primary)); }
.po-mod--tb .po-mod__ico { background: rgb(var(--success) / .12); color: rgb(var(--success)); }
.po-mod__nom { font-weight: 700; color: #0f172a; font-size: .98rem; display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; }
.po-badge { font-size: .62rem; font-weight: 700; padding: .12rem .45rem; border-radius: 99px; background: rgb(var(--danger)); color: #fff; letter-spacing: .02em; }
.po-mod__promesse { font-size: .82rem; color: #64748b; margin-top: .2rem; line-height: 1.4; }
.po-mod__liste { list-style: none; margin: 0; padding: .2rem 1rem .6rem; flex: 1; }
.po-mod__liste li { display: flex; gap: .6rem; padding: .55rem 0; border-top: 1px solid #f1f4f8; }
.po-mod__liste li:first-child { border-top: 0; }
.po-mod__liste i { color: rgb(var(--primary)); font-size: 1.05rem; margin-top: .05rem; }
.po-mod--tb .po-mod__liste i { color: rgb(var(--success)); }
.po-mod__liste b { display: block; color: #0f172a; font-size: .84rem; }
.po-mod__liste span { font-size: .78rem; color: #64748b; line-height: 1.4; }
.po-mod__pied { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: space-between; padding: .75rem 1rem; border-top: 1px solid #e2e8f0; background: #f8fafc; border-radius: 0 0 .625rem .625rem; }
.po-mod__ou { font-size: .72rem; color: #94a3b8; }
.po-btn { display: inline-flex; align-items: center; gap: .35rem; height: 34px; padding: 0 .85rem; border-radius: .5rem; font-size: .78rem; font-weight: 600; color: #fff !important; background: rgb(var(--primary)); text-decoration: none; }
.po-btn:hover { background: rgb(var(--primary) / .9); }
.po-mod--tb .po-btn { background: rgb(var(--success)); }
.po-mod--tb .po-btn:hover { background: rgb(var(--success) / .9); }
.po__bas { display: flex; flex-wrap: wrap; gap: .5rem; justify-content: space-between; align-items: center; padding: 0 1.25rem 1rem; font-size: .74rem; color: #94a3b8; }
.po__bas i { margin-right: .25rem; }
@media (max-width: 900px) { .po__grille { grid-template-columns: 1fr; } }
@media (max-width: 560px) {
    .po__tete, .po__grille, .po__bas { padding-left: .85rem; padding-right: .85rem; }
    .po-mod__pied { flex-direction: column; align-items: stretch; }
    .po-btn { justify-content: center; }
}
</style>
